import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Save,
  Publish,
  Public,
  ContentCopy,
  LinkedIn as LinkedInIcon,
} from "@mui/icons-material";
import TipTapEditor from "@/components/TipTapEditor";
import { useArticle, useCreateArticle, useUpdateArticle } from "@/hooks/useKB";
import {
  KB_CATEGORY_LABELS,
  WEBSITE_CATEGORY_LABELS,
  type KBCategory,
  type KBStatus,
  type WebsiteCategory,
} from "@/types";
import ChipListEditor from "@/components/ChipListEditor";

export default function KBEditor() {
  const { articleId } = useParams<{ articleId: string }>();
  const isNew = !articleId;
  const navigate = useNavigate();

  const { data: existing, isLoading } = useArticle(isNew ? undefined : articleId);
  const createMutation = useCreateArticle();
  const updateMutation = useUpdateArticle(articleId ?? "");

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<KBCategory>("ideas_notes_snippets");
  const [status, setStatus] = useState<KBStatus>("draft");
  const [tags, setTags] = useState<string[]>([]);
  const [contentHtml, setContentHtml] = useState("");
  const [contentMarkdown, setContentMarkdown] = useState("");
  const [changeSummary, setChangeSummary] = useState("");
  const [websitePublished, setWebsitePublished] = useState(false);
  const [websiteSlug, setWebsiteSlug] = useState("");
  const [websiteCategory, setWebsiteCategory] = useState<WebsiteCategory | "">("");
  const [websiteExcerpt, setWebsiteExcerpt] = useState("");
  const [websiteHeroImageUrl, setWebsiteHeroImageUrl] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setCategory(existing.category);
      setStatus(existing.status);
      setTags(existing.tags ?? []);
      setContentHtml(existing.content_html);
      setContentMarkdown(existing.content_markdown);
      setWebsitePublished(existing.website_published);
      setWebsiteSlug(existing.website_slug ?? "");
      setWebsiteCategory(existing.website_category ?? "");
      setWebsiteExcerpt(existing.website_excerpt ?? "");
      setWebsiteHeroImageUrl(existing.website_hero_image_url ?? "");
    }
  }, [existing]);

  const handleSave = async (publishStatus?: KBStatus) => {
    const data = {
      title,
      category,
      status: publishStatus ?? status,
      tags: tags.length > 0 ? tags : undefined,
      content_html: contentHtml,
      content_markdown: contentMarkdown,
      website_published: websitePublished,
      website_slug: websiteSlug || null,
      website_category: websiteCategory || null,
      website_excerpt: websiteExcerpt || null,
      website_hero_image_url: websiteHeroImageUrl || null,
      ...(isNew ? {} : { change_summary: changeSummary || undefined }),
    };

    try {
      if (isNew) {
        const article = await createMutation.mutateAsync(data);
        navigate(`/kb/${article.id}`);
      } else {
        await updateMutation.mutateAsync(data);
        setSnackbar({ open: true, message: "Article saved", severity: "success" });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      console.error("KB save error:", err);
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };

  if (!isNew && isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  // LinkedIn posts get a "Copy for LinkedIn" shortcut — strips markdown
  // formatting and trims to LinkedIn's 3,000-char hard limit. We use the
  // markdown source rather than HTML so the result is human-text without
  // requiring a separate HTML→text pass.
  const isLinkedInPost = websiteCategory === "linkedin_post";
  const copyForLinkedIn = async () => {
    const text = contentMarkdown
      .replace(/^#{1,6}\s+/gm, "") // strip heading markers
      .replace(/\*\*(.+?)\*\*/g, "$1") // strip bold
      .replace(/__(.+?)__/g, "$1")
      .replace(/[*_](.+?)[*_]/g, "$1") // strip italics
      .replace(/`([^`]+)`/g, "$1") // strip inline code
      .replace(/^[-*]\s+/gm, "• ") // bullets → •
      .trim();
    const truncated = text.length > 3000 ? text.slice(0, 2997) + "..." : text;
    try {
      await navigator.clipboard.writeText(truncated);
      setSnackbar({
        open: true,
        message: `Copied ${truncated.length}/3000 chars to clipboard`,
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Copy failed — select + copy manually",
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(isNew ? "/kb" : `/kb/${articleId}`)}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          {isNew ? "New Article" : `Edit: ${existing?.title ?? ""}`}
        </Typography>
        {isLinkedInPost && (
          <Button
            variant="outlined"
            startIcon={<LinkedInIcon />}
            endIcon={<ContentCopy fontSize="small" />}
            onClick={copyForLinkedIn}
            disabled={!contentMarkdown.trim()}
            title="Copy plain-text version (LinkedIn 3,000-char limit) to clipboard"
          >
            Copy for LinkedIn
          </Button>
        )}
        {!isNew && status === "draft" && (
          <Button
            variant="outlined"
            startIcon={<Publish />}
            onClick={() => handleSave("published")}
            disabled={isPending || !title}
          >
            Publish
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={() => handleSave()}
          disabled={isPending || !title}
        >
          {isPending ? "Saving..." : "Save"}
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 300px" }, gap: 2 }}>
        {/* Main editor */}
        <Box>
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ style: { fontSize: "1.3rem", fontFamily: "'Playfair Display', serif" } }}
          />
          <TipTapEditor
            content={contentHtml}
            onChange={(html, md) => {
              setContentHtml(html);
              setContentMarkdown(md);
            }}
          />
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Category
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as KBCategory)}
                >
                  {Object.entries(KB_CATEGORY_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select value={status} onChange={(e) => setStatus(e.target.value as KBStatus)}>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <ChipListEditor label="Tags" values={tags} onChange={setTags} />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">Website publishing</Typography>
                {existing?.website_published && (
                  <Chip
                    icon={<Public sx={{ fontSize: "1rem !important" }} />}
                    label="LIVE"
                    size="small"
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={websitePublished}
                    onChange={(e) => setWebsitePublished(e.target.checked)}
                  />
                }
                label="Publish to elitemind.co.uk"
              />
              {websitePublished && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Website category</InputLabel>
                    <Select
                      label="Website category"
                      value={websiteCategory}
                      onChange={(e) => setWebsiteCategory(e.target.value as WebsiteCategory)}
                    >
                      {Object.entries(WEBSITE_CATEGORY_LABELS).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    size="small"
                    label="URL slug"
                    placeholder="e.g. about-elite-mind"
                    value={websiteSlug}
                    onChange={(e) => setWebsiteSlug(e.target.value)}
                    helperText="The bit after / in the URL. Lowercase, hyphens only."
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Excerpt"
                    placeholder="Short summary for listings/cards"
                    value={websiteExcerpt}
                    onChange={(e) => setWebsiteExcerpt(e.target.value)}
                    multiline
                    minRows={2}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Hero image URL"
                    placeholder="https://..."
                    value={websiteHeroImageUrl}
                    onChange={(e) => setWebsiteHeroImageUrl(e.target.value)}
                  />
                  {status !== "published" && (
                    <Alert severity="info" sx={{ mt: 0.5 }}>
                      Article must also have status &quot;Published&quot; to actually appear on the
                      site.
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {!isNew && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Change Summary
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={changeSummary}
                  onChange={(e) => setChangeSummary(e.target.value)}
                  placeholder="What changed? (optional)"
                  multiline
                  minRows={2}
                />
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
